import React, { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  completeFanvueOAuth,
  completeFanvueOAuthByState,
  completeFanvueSignupOAuth,
  getApiErrorMessage,
  hasValidStoredAuthSession,
  storeAuthSession,
} from '../../api'
import { LoadingBlock, Section, StatusPill } from '../../components/v2/V2Primitives'
import PublicSiteShell from '../../components/v2/PublicSiteShell'
import { clearFanvueOAuthSession, getFanvueOAuthSession } from './fanvueOAuthState'
import '../../styles/admin-v2.css'

function canRetryByState(error) {
  const status = Number(error?.status || 0)
  const message = String(error?.message || '').toLowerCase()
  return [400, 404].includes(status) && (
    message.includes('state') ||
    message.includes('session') ||
    message.includes('started')
  )
}

function providerErrorMessage(error, description) {
  if (String(error || '').toLowerCase() === 'access_denied') {
    return 'Authorization was cancelled. No Fanvue data was connected.'
  }
  const value = String(description || error || 'Fanvue authorization failed.')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return value.slice(0, 320)
}

export default function AdminV2FanvueCallback({ publicShell = false }) {
  const [params] = useSearchParams()
  const code = params.get('code') || ''
  const state = params.get('state') || ''
  const providerError = params.get('error') || ''
  const providerDescription = params.get('error_description') || ''
  const sessionRef = useRef(null)
  const [modelId, setModelId] = useState('')
  const [result, setResult] = useState({ status: 'pending', message: '' })

  useEffect(() => {
    let alive = true
    async function complete() {
      if (providerError) {
        setResult({
          status: 'error',
          message: providerErrorMessage(providerError, providerDescription),
        })
        return
      }
      if (!code || !state) {
        setResult({
          status: 'error',
          message: 'Fanvue did not return an authorization code and state.',
        })
        return
      }
      if (!sessionRef.current) {
        sessionRef.current = getFanvueOAuthSession(state)
        setModelId(sessionRef.current?.modelId || '')
      }
      const session = sessionRef.current
      try {
        let updatedModel = null
        const useSignupComplete = session?.source === 'signup' || !hasValidStoredAuthSession()
        if (useSignupComplete) {
          const signup = await completeFanvueSignupOAuth({ code, state })
          storeAuthSession(signup, { remember: true })
          updatedModel = signup?.model || null
        } else if (session?.modelId) {
          try {
            updatedModel = await completeFanvueOAuth(session.modelId, { code, state })
          } catch (err) {
            if (!canRetryByState(err)) throw err
            updatedModel = await completeFanvueOAuthByState({ code, state })
          }
        } else {
          updatedModel = await completeFanvueOAuthByState({ code, state })
        }
        const nextModelId = session?.modelId || updatedModel?.id || updatedModel?.model_id || ''
        if (nextModelId) setModelId(String(nextModelId))
        clearFanvueOAuthSession(state)
        if (alive) {
          setResult({
            status: 'success',
            message: useSignupComplete
              ? 'Fanvue account connected. Review your workspace setup before enabling the paused bot.'
              : 'Fanvue account connected.',
          })
        }
      } catch (err) {
        if (alive) {
          setResult({
            status: 'error',
            message: getApiErrorMessage(err, 'Failed to complete Fanvue OAuth'),
          })
        }
      }
    }
    complete()
    return () => { alive = false }
  }, [code, state, providerError, providerDescription])

  if (result.status === 'pending') {
    const pending = <LoadingBlock label="Completing Fanvue OAuth..." />
    return publicShell ? <PublicSiteShell><div className="v2-public-result">{pending}</div></PublicSiteShell> : pending
  }

  const ok = result.status === 'success'
  if (publicShell) {
    return (
      <PublicSiteShell>
        <section className="v2-public-result-card" aria-live="polite">
          <p className="v2-kicker">Fanvue OAuth</p>
          <h1>{ok ? 'Fanvue connected' : 'Connection needs attention'}</h1>
          <div className={ok ? 'v2-success' : 'v2-error'}>{result.message}</div>
          <div className="v2-public-actions">
            <Link className="v2-button" to={ok ? '/v2/connections' : '/connect/fanvue'}>
              {ok ? 'Open Connections' : 'Try again'}
            </Link>
            <Link className="v2-button v2-button-quiet" to="/support">Get support</Link>
          </div>
        </section>
      </PublicSiteShell>
    )
  }

  return (
    <div className="v2-stack">
      <Section
        eyebrow="Fanvue OAuth"
        title={ok ? 'Fanvue connected' : 'Fanvue connection needs attention'}
        description={result.message}
        actions={<StatusPill tone={ok ? 'good' : 'bad'}>{ok ? 'connected' : 'failed'}</StatusPill>}
      >
        <div className={ok ? 'v2-success' : 'v2-error'}>{result.message}</div>
        <div className="v2-action-row">
          <Link className="v2-button" to="/v2/connections">Back to Connections</Link>
          {modelId ? <Link className="v2-text-link" to={`/v2/models/${modelId}`}>Open model</Link> : null}
        </div>
      </Section>
    </div>
  )
}
